import {
  useEffect, useLayoutEffect, useMemo, useRef, useState,
  type FormEvent, type KeyboardEvent,
} from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  X, ArrowLeft, Plus, Send, MessageSquare, Sparkles, Trash2,
  AlertCircle, Square, Clock, Globe,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  useChatConversations, useChatConversation, useCreateChatConversation,
  useSendChatMessage, useArchiveChatConversation, useProperty,
  type ChatMessage, type ChatConversation,
} from "@/lib/queries";
import { cn } from "@/lib/utils";


/**
 * Extract a property_id from the current URL if we're on a property
 * page (`/p/:propertyId/...`). Returns null on non-property pages so
 * the chat creates an unscoped conversation. The chat backend uses
 * this to inject that property's full dossier into every prompt —
 * Auriel's situational-awareness layer.
 */
function usePropertyIdFromRoute(): number | null {
  const { pathname } = useLocation();
  // Match /p/<digits>/ or /p/<digits> at end-of-path
  const m = pathname.match(/^\/p\/(\d+)(?:\/|$)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
}

// ─── time formatters ────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const dt = (Date.now() - new Date(iso).getTime()) / 1000;
  if (dt < 60)        return "just now";
  if (dt < 3600)      return `${Math.floor(dt / 60)}m ago`;
  if (dt < 86_400)    return `${Math.floor(dt / 3600)}h ago`;
  return `${Math.floor(dt / 86_400)}d ago`;
}

function exactTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString([], {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

// ─── message bubble ─────────────────────────────────────────────────

function MessageBubble({ m }: { m: ChatMessage }) {
  const isUser = m.role === "user";
  return (
    <div
      className={cn(
        "group flex w-full",
        isUser ? "justify-end" : "justify-start",
      )}
    >
      <div className={cn("flex max-w-[88%] flex-col gap-1", isUser && "items-end")}>
        <div
          className={cn(
            "rounded-2xl px-3.5 py-2 text-sm leading-snug",
            isUser
              ? "rounded-br-sm bg-primary text-primary-foreground"
              : "rounded-bl-sm bg-muted text-foreground",
          )}
        >
          {isUser ? (
            <div className="whitespace-pre-wrap">{m.content}</div>
          ) : (
            <div className={cn(
              "prose prose-sm max-w-none",
              "text-foreground prose-headings:text-foreground prose-strong:text-foreground",
              "prose-a:text-primary prose-li:text-foreground prose-p:text-foreground",
              "prose-code:bg-background prose-code:text-foreground",
              "prose-code:rounded prose-code:px-1 prose-code:py-0.5",
              "prose-code:border prose-code:border-border",
              "prose-code:before:hidden prose-code:after:hidden",
              "prose-code:font-normal",
              "prose-pre:bg-background prose-pre:text-foreground",
              "prose-pre:border prose-pre:border-border",
              "prose-pre:rounded-md",
              "dark:prose-invert dark:prose-pre:bg-muted dark:prose-pre:text-foreground",
              "dark:prose-code:bg-muted dark:prose-code:text-foreground",
            )}>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
            </div>
          )}
        </div>
        <div
          className={cn(
            "flex items-center gap-2 px-1 text-[10px] text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100",
            isUser ? "justify-end" : "justify-start",
          )}
        >
          <span title={exactTime(m.created_at)}>{relativeTime(m.created_at)}</span>
          {!isUser && m.duration_s !== null && (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-2.5 w-2.5" />
              {m.duration_s}s
            </span>
          )}
          {!isUser && m.ok === false && (
            <span className="inline-flex items-center gap-1 text-red-600 opacity-100 dark:text-red-400">
              <AlertCircle className="h-3 w-3" />
              {m.error || "failed"}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── thinking bubble (assistant-side typing indicator) ──────────────

function ThinkingBubble({ elapsed, onStop }: { elapsed: number; onStop?: () => void }) {
  return (
    <div className="flex w-full justify-start">
      <div className="flex max-w-[88%] flex-col gap-1">
        <div className="flex items-center gap-2 rounded-2xl rounded-bl-sm bg-muted px-3.5 py-2.5 text-sm">
          <span className="flex items-center gap-1">
            <span className="block h-1.5 w-1.5 animate-pulse rounded-full bg-primary [animation-delay:-0.3s]" />
            <span className="block h-1.5 w-1.5 animate-pulse rounded-full bg-primary [animation-delay:-0.15s]" />
            <span className="block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
          </span>
          <span className="text-xs text-muted-foreground">
            {elapsed < 5 ? "Auriel is thinking" :
             elapsed < 30 ? "still working" :
             elapsed < 120 ? "this one's taking a moment" :
             "long-running task"}
            {elapsed > 0 && ` · ${Math.round(elapsed)}s`}
          </span>
        </div>
        {onStop && elapsed >= 5 && (
          <button
            type="button"
            onClick={onStop}
            className="inline-flex items-center gap-1 self-start rounded-md border border-border px-2 py-0.5 text-[10px] text-muted-foreground hover:bg-accent hover:text-foreground"
            title="Stop generation"
          >
            <Square className="h-2.5 w-2.5" /> stop
          </button>
        )}
      </div>
    </div>
  );
}

// ─── auto-grow textarea ─────────────────────────────────────────────

function AutoTextarea({
  value, onChange, onKeyDown, disabled, placeholder, maxRows = 8,
}: {
  value: string;
  onChange: (v: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLTextAreaElement>) => void;
  disabled?: boolean;
  placeholder?: string;
  maxRows?: number;
}) {
  const ref = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const t = ref.current;
    if (!t) return;
    // Reset before measuring so it can shrink too.
    t.style.height = "0px";
    const lineHeight = parseFloat(getComputedStyle(t).lineHeight || "20");
    const max = lineHeight * maxRows;
    const next = Math.min(t.scrollHeight, max);
    t.style.height = `${Math.max(40, next)}px`;
    t.style.overflowY = t.scrollHeight > max ? "auto" : "hidden";
  }, [value, maxRows]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      disabled={disabled}
      placeholder={placeholder}
      rows={1}
      className="flex-1 resize-none rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
    />
  );
}

// ─── empty-state suggestions ────────────────────────────────────────

const STARTERS: { label: string; prompt: string }[] = [
  {
    label: "Audit a property",
    prompt: "Run an SEO audit on nws.ro and walk me through the top findings.",
  },
  {
    label: "Scan a single URL",
    prompt: "Get the frog to scan https://example.com — tell me what he finds.",
  },
  {
    label: "Lighthouse perf check",
    prompt: "Check Lighthouse performance + Core Web Vitals on https://example.com.",
  },
  {
    label: "Compare backlinks",
    prompt: "Compare Ahrefs vs DataForSEO backlink counts for nws.ro and explain the divergence.",
  },
];

// ─── conversation list ─────────────────────────────────────────────

function ConversationList({
  onPick, onNew,
}: {
  onPick: (id: number) => void;
  onNew: () => void;
}) {
  const list = useChatConversations();
  const archive = useArchiveChatConversation();
  const items = list.data?.items ?? [];

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-border p-3">
        <button
          type="button"
          onClick={onNew}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" /> New conversation
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {list.isLoading && (
          <div className="p-4 text-sm text-muted-foreground">Loading…</div>
        )}
        {!list.isLoading && items.length === 0 && (
          <div className="p-6 text-center">
            <MessageSquare className="mx-auto h-6 w-6 text-muted-foreground" />
            <p className="mt-2 text-xs text-muted-foreground">
              No conversations yet. Start one above.
            </p>
          </div>
        )}
        {items.map((c) => (
          <div
            key={c.id}
            className="group flex items-start gap-2 border-b border-border px-3 py-2 hover:bg-accent/40"
          >
            <button
              type="button"
              onClick={() => onPick(c.id)}
              className="min-w-0 flex-1 text-left"
            >
              <div className="truncate text-sm font-medium">{c.title}</div>
              <div className="text-[11px] text-muted-foreground">
                {relativeTime(c.last_message_at)}
              </div>
            </button>
            <button
              type="button"
              onClick={async () => {
                if (!confirm("Archive this conversation?")) return;
                await archive.mutateAsync(c.id);
              }}
              title="Archive"
              className="rounded-md p-1 text-muted-foreground opacity-0 hover:bg-accent group-hover:opacity-100"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── conversation view ─────────────────────────────────────────────

function ConversationView({
  conversationId, onBack,
}: {
  conversationId: number;
  onBack: () => void;
}) {
  const qc = useQueryClient();
  const conv = useChatConversation(conversationId);
  const send = useSendChatMessage();

  const [input, setInput] = useState("");
  // While the stream is in flight: show user's message immediately even
  // before the backend has echoed `user_message`. This guarantees the
  // bubble is on screen between submit and persistence.
  const [optimisticUserMsg, setOptimisticUserMsg] = useState<string | null>(null);
  const [thinkingElapsed, setThinkingElapsed] = useState<number | null>(null);
  const [thinkingStartedAt, setThinkingStartedAt] = useState<number | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Inject a fresh message into the cached conversation so the UI shows
  // it the instant the stream emits it — no full refetch needed.
  const injectMessage = (msg: ChatMessage) => {
    qc.setQueryData<ChatConversation | undefined>(
      ["chat", "conversation", conversationId],
      (prev) => {
        if (!prev) return prev;
        // Dedup by id in case the refetch raced us.
        if (prev.messages.some((m) => m.id === msg.id)) return prev;
        return {
          ...prev,
          messages: [...prev.messages, msg],
          last_message_at: msg.created_at,
        };
      },
    );
  };

  // Local elapsed-time ticker so seconds advance smoothly between
  // backend keepalives (which fire every 5s).
  useEffect(() => {
    if (thinkingStartedAt === null) return;
    const id = setInterval(() => {
      setThinkingElapsed(Math.floor((Date.now() - thinkingStartedAt) / 1000));
    }, 250);
    return () => clearInterval(id);
  }, [thinkingStartedAt]);

  // Auto-scroll to bottom on new content.
  useLayoutEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [
    conv.data?.messages.length,
    optimisticUserMsg,
    thinkingElapsed,
    streamError,
  ]);

  const onSubmit = async (e?: FormEvent) => {
    if (e) e.preventDefault();
    const content = input.trim();
    if (!content || send.isPending) return;
    setInput("");
    setOptimisticUserMsg(content);
    setThinkingElapsed(0);
    setThinkingStartedAt(Date.now());
    setStreamError(null);

    const ac = new AbortController();
    abortRef.current = ac;

    try {
      await send.mutateAsync({
        conversationId,
        content,
        signal: ac.signal,
        onEvent: (ev) => {
          if (ev.type === "user_message") {
            // Real persisted user message — push into cache, drop optimistic.
            injectMessage(ev.message);
            setOptimisticUserMsg(null);
          } else if (ev.type === "thinking") {
            // Backend keepalive — local ticker handles seconds, but still
            // accept the backend's elapsed in case of drift.
            if (ev.elapsed_s > (thinkingElapsed ?? 0)) {
              setThinkingElapsed(ev.elapsed_s);
            }
          } else if (ev.type === "assistant_message") {
            injectMessage(ev.message);
            setThinkingStartedAt(null);
            setThinkingElapsed(null);
          } else if (ev.type === "error") {
            setStreamError(ev.error);
            setThinkingStartedAt(null);
            setThinkingElapsed(null);
          }
        },
      });
    } catch (err) {
      const aborted = ac.signal.aborted;
      setStreamError(
        aborted
          ? "Cancelled — Auriel may still finish in the background."
          : (err instanceof Error ? err.message : "Send failed"),
      );
    } finally {
      // Optimistic message stays only if backend never echoed it; in any
      // case it'll be in the cache from injectMessage by now.
      setOptimisticUserMsg(null);
      setThinkingStartedAt(null);
      setThinkingElapsed(null);
      abortRef.current = null;
    }
  };

  const onStop = () => {
    abortRef.current?.abort();
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void onSubmit();
    }
  };

  const messages = conv.data?.messages ?? [];
  const isEmpty = !conv.isLoading && messages.length === 0 && optimisticUserMsg === null;

  // Place the optimistic user bubble at the end. Built as a synthetic
  // message-shaped object; it has id=-1 so MessageBubble handles it
  // identically to a real one (no flicker on swap because injectMessage
  // adds the real one with the real id, and we then drop the optimistic).
  const optimisticBubble = useMemo<ChatMessage | null>(() => {
    if (!optimisticUserMsg) return null;
    return {
      id: -1,
      role: "user",
      content: optimisticUserMsg,
      created_at: new Date().toISOString(),
      audit_id: null,
      duration_s: null,
      ok: null,
      error: null,
    };
  }, [optimisticUserMsg]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <button
          type="button"
          onClick={onBack}
          className="rounded-md p-1 text-muted-foreground hover:bg-accent"
          title="Back to conversations"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold">
            {conv.data?.title ?? "…"}
          </div>
          {messages.length > 0 && (
            <div className="text-[10px] text-muted-foreground">
              {messages.length} message{messages.length === 1 ? "" : "s"}
            </div>
          )}
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-3">
        {conv.isLoading && (
          <div className="text-sm text-muted-foreground">Loading…</div>
        )}

        {isEmpty && (
          <div className="flex h-full flex-col items-center justify-center gap-4 px-4">
            <div className="rounded-full bg-primary/10 p-3">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium">Ask Auriel anything</p>
              <p className="mt-1 text-xs text-muted-foreground">
                SEO audits, page scans, Core Web Vitals, backlink checks — all wired in.
              </p>
            </div>
            <div className="grid w-full grid-cols-1 gap-1.5">
              {STARTERS.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  onClick={() => setInput(s.prompt)}
                  className="rounded-md border border-border bg-card px-3 py-2 text-left text-xs hover:bg-accent/60"
                >
                  <span className="font-medium">{s.label}</span>
                  <span className="ml-2 text-muted-foreground">{s.prompt}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => <MessageBubble key={m.id} m={m} />)}

        {optimisticBubble && (
          <MessageBubble m={optimisticBubble} />
        )}

        {thinkingElapsed !== null && (
          <ThinkingBubble elapsed={thinkingElapsed} onStop={onStop} />
        )}

        {streamError && (
          <div className="rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-700 dark:text-red-400">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <div className="flex-1">{streamError}</div>
            </div>
          </div>
        )}
      </div>

      <form onSubmit={onSubmit} className="border-t border-border p-3">
        <div className="flex items-end gap-2">
          <AutoTextarea
            value={input}
            onChange={setInput}
            onKeyDown={onKeyDown}
            disabled={send.isPending}
            placeholder="Ask Auriel… (Enter to send · Shift+Enter for newline)"
          />
          {send.isPending ? (
            <button
              type="button"
              onClick={onStop}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted text-foreground hover:bg-accent"
              title="Stop"
            >
              <Square className="h-4 w-4" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!input.trim()}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              title="Send"
            >
              <Send className="h-4 w-4" />
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

// ─── panel shell ───────────────────────────────────────────────────

export function ChatPanel({
  open, onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [activeId, setActiveId] = useState<number | null>(null);
  const create = useCreateChatConversation();
  // Auto-scope new conversations to the property the user is viewing.
  // Backend uses this to inject the full property dossier into every
  // prompt — see tyrael.context.dossier.
  const scopePropertyId = usePropertyIdFromRoute();
  const scopeProperty = useProperty(scopePropertyId);

  useEffect(() => {
    if (!open) setActiveId(null);
  }, [open]);

  const onNew = async () => {
    const r = await create.mutateAsync({
      property_id: scopePropertyId ?? undefined,
    });
    setActiveId(r.id);
  };

  return (
    <>
      <div
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-40 bg-black/30 transition-opacity",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />
      <aside
        className={cn(
          "fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-border bg-card shadow-2xl transition-transform",
          open ? "translate-x-0" : "translate-x-full",
        )}
      >
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <div className="flex-1">
            <h2 className="text-sm font-semibold leading-tight">Auriel</h2>
            {scopePropertyId && scopeProperty.data && (
              <p className="flex items-center gap-1 text-[10px] text-muted-foreground">
                <Globe className="h-2.5 w-2.5" />
                scoped to <span className="font-mono">{scopeProperty.data.domain}</span>
                {" — full dossier auto-loaded"}
              </p>
            )}
            {!scopePropertyId && (
              <p className="text-[10px] text-muted-foreground">
                general chat — open from a property page for full dossier
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            title="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden">
          {activeId === null ? (
            <ConversationList
              onPick={(id) => setActiveId(id)}
              onNew={onNew}
            />
          ) : (
            <ConversationView
              conversationId={activeId}
              onBack={() => setActiveId(null)}
            />
          )}
        </div>
      </aside>
    </>
  );
}
